import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { apiService, formatApiError } from '../services/api';

const STATUS_TEXT = {
  submitted: 'Submitted — waiting for review',
  reviewing: 'Our team is reviewing your story',
  approved: 'Approved — we will contact you before publishing',
  declined: 'Not selected this time',
  withdrawn: 'Withdrawn — will not be published',
};

const EMPTY = { story: '', partner_first_name: '', together_since: '', display_name: 'first_name_initial', consent_publish: false, consent_contact: false, consent_photo: false };

const ShareStoryCard = () => {
  const { toast } = useToast();
  const [stories, setStories] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => apiService.stories.mine().then((r) => setStories(r.data.data.stories)).catch(() => setStories([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pending = (stories || []).find((s) => ['submitted', 'reviewing', 'approved'].includes(s.status));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiService.stories.submit({ ...form, partner_first_name: form.partner_first_name || null, together_since: form.together_since || null });
      toast({ title: 'Story submitted', description: r.data.message });
      setForm(EMPTY); setOpen(false); load();
    } catch (err) { toast({ title: 'Could not submit', description: formatApiError(err), variant: 'destructive' }); } finally { setBusy(false); }
  };

  const withdraw = async (id) => {
    try { await apiService.stories.withdraw(id); toast({ title: 'Story withdrawn' }); load(); } catch (err) { toast({ title: 'Could not withdraw', description: formatApiError(err), variant: 'destructive' }); }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="share-story-card">
      <div>
        <h2 className="text-lg font-semibold flex items-center"><Sparkles className="w-5 h-5 mr-2 text-rose-500" />Share your FlameLink story</h2>
        <p className="text-sm text-gray-600 mt-1">Met someone special here? Tell us about it. Stories are only published with your written consent, and we will contact you first.</p>
      </div>

      {stories && stories.length > 0 && (
        <ul className="space-y-2" data-testid="my-stories-list">
          {stories.slice(0, 3).map((s) => (
            <li key={s.id} className="rounded-lg bg-rose-50 p-3 text-sm" data-testid={`my-story-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium" data-testid={`my-story-status-${s.id}`}>{STATUS_TEXT[s.status] || s.status}</span>
                {['submitted', 'reviewing', 'approved'].includes(s.status) && (
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => withdraw(s.id)} data-testid={`my-story-withdraw-${s.id}`}>Withdraw consent</Button>
                )}
              </div>
              <p className="text-gray-600 mt-1 line-clamp-2">{s.story}</p>
            </li>
          ))}
        </ul>
      )}

      {!pending && !open && <Button variant="outline" onClick={() => setOpen(true)} data-testid="share-story-open-btn">Share my story</Button>}

      {open && !pending && (
        <form onSubmit={submit} className="space-y-4" data-testid="share-story-form">
          <Textarea value={form.story} onChange={(e) => set('story', e.target.value)} rows={5} maxLength={2000} required minLength={40}
            placeholder="How did you meet on FlameLink, and what happened next? (at least 40 characters)" data-testid="story-text-input" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={form.partner_first_name} onChange={(e) => set('partner_first_name', e.target.value)} maxLength={40} placeholder="Partner's first name (optional)" data-testid="story-partner-input" />
            <Input value={form.together_since} onChange={(e) => set('together_since', e.target.value)} maxLength={40} placeholder="Together since (optional, e.g. March 2026)" data-testid="story-since-input" />
          </div>
          <div>
            <label className="text-sm text-gray-700 block mb-1">How should we show your name?</label>
            <select value={form.display_name} onChange={(e) => set('display_name', e.target.value)} className="border rounded-md px-3 py-2 bg-white text-sm w-full" data-testid="story-display-name-select">
              <option value="first_name_initial">First name + last initial (e.g. "Sarah M.")</option>
              <option value="first_name">First name only</option>
              <option value="anonymous">Anonymous</option>
            </select>
          </div>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2"><Checkbox checked={form.consent_publish} onCheckedChange={(v) => set('consent_publish', !!v)} data-testid="story-consent-publish" /><span>I confirm this story is true, I am an adult FlameLink member, and I give FlameLink permission to publish it (edited for length) on flamelink.site and in marketing. <b>Required.</b></span></label>
            <label className="flex items-start gap-2"><Checkbox checked={form.consent_contact} onCheckedChange={(v) => set('consent_contact', !!v)} data-testid="story-consent-contact" /><span>FlameLink may email me about this story before publishing.</span></label>
            <label className="flex items-start gap-2"><Checkbox checked={form.consent_photo} onCheckedChange={(v) => set('consent_photo', !!v)} data-testid="story-consent-photo" /><span>I'm open to sharing a photo later (we will ask separately; nothing is used from my profile automatically).</span></label>
          </div>
          <p className="text-xs text-gray-500">You can withdraw consent any time from this page. Partner names are only published with their separate consent.</p>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !form.consent_publish || form.story.trim().length < 40} data-testid="story-submit-btn">{busy ? 'Submitting…' : 'Submit story'}</Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="story-cancel-btn">Cancel</Button>
          </div>
        </form>
      )}
    </Card>
  );
};

export default ShareStoryCard;
